import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-[#EDECEA]" data-testid="terms-page">
      <Navigation />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-3xl mx-auto px-6 md:px-12 pt-32 pb-20"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#9B9589] hover:text-[#0F1B2D] transition-colors mb-10"
          data-testid="terms-back-link"
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <div className="h-[2px] w-10 bg-[#C4A35A]" />
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#9B9589] font-bold">
            Legal
          </p>
        </div>

        <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0F1B2D] tracking-tight mb-3">
          Terms of Service
        </h1>
        <p className="text-sm font-medium text-[#9B9589] mb-12">
          Last updated: January 2025 &middot; Effective immediately upon publication
        </p>

        <div className="space-y-10">
          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              1. Acceptance of Terms
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
              By accessing, browsing, or using the services provided by the Artist Identification Network & Accreditation Archive ("AINAA", "the Association"), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you must discontinue use of our platform immediately.
            </p>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              2. Nature of the Association
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
              AINAA is a professional credibility body for creative professionals. It is not a marketplace, talent agency, employment platform, or social network. Membership in AINAA is a professional credential indicating that the individual has been verified by the Association's standards. AINAA does not guarantee employment, commissions, or commercial outcomes for its members.
            </p>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              3. Membership & Verification
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed mb-4">
              Membership is by verification only. Applicants must be recommended or verified by an existing trusted member of the AINAA network. The Association reserves the right to:
            </p>
            <ul className="list-disc list-inside text-sm text-[#4A4640] font-medium space-y-2 ml-4">
              <li>Accept or reject any membership application at its sole discretion</li>
              <li>Suspend or revoke membership for conduct that violates the Association's Code of Professional Conduct</li>
              <li>Modify the verification criteria and process from time to time</li>
              <li>Require additional documentation or references during the verification process</li>
            </ul>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              4. Trust Score
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed mb-4">
              The Trust Score is a proprietary metric calculated by AINAA's Introwspect&trade; algorithm. By using AINAA's services, you agree that:
            </p>
            <ul className="list-disc list-inside text-sm text-[#4A4640] font-medium space-y-2 ml-4">
              <li>The Trust Score is based on verified professional data and behavioural analysis, not subjective opinions</li>
              <li>The Trust Score is dynamic and may increase or decrease based on ongoing professional conduct</li>
              <li>AINAA's calculation methodology may be revised from time to time, with notice provided via official circulars</li>
              <li>You will not attempt to manipulate, falsify, or artificially inflate your Trust Score or that of another member</li>
              <li>The Trust Score is the property of AINAA and is licensed to you for personal and professional use only</li>
            </ul>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              5. User Responsibilities
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed mb-4">
              As a user or member of AINAA, you agree to:
            </p>
            <ul className="list-disc list-inside text-sm text-[#4A4640] font-medium space-y-2 ml-4">
              <li>Provide accurate, truthful, and complete information during application and throughout your membership</li>
              <li>Maintain the security and confidentiality of your account credentials</li>
              <li>Not misrepresent your membership status, Trust Score, or Association credentials</li>
              <li>Respect the privacy and confidentiality of other members</li>
              <li>Comply with the Association's Code of Professional Conduct and all applicable laws</li>
              <li>Notify AINAA promptly of any unauthorised access to your account</li>
            </ul>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              6. Specter&trade; AI Assistant
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
              Specter&trade; is an AI-powered assistant provided to AINAA members. Recommendations, advice, and insights provided by Specter&trade; are informational in nature and do not constitute professional legal, financial, or medical advice. AINAA is not liable for decisions made based on Specter&trade; recommendations. For formal legal or financial matters, members should consult the AINAA Legal Aid Panel or a qualified professional.
            </p>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              7. Intellectual Property
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
              All content, features, functionality, trademarks, and intellectual property of the AINAA platform — including but not limited to the AINAA name, logo, Trust Score methodology, Introwspect&trade;, and Specter&trade; — are the exclusive property of AINAA and are protected by applicable intellectual property laws. Members may not reproduce, distribute, or create derivative works from AINAA's proprietary materials without prior written consent.
            </p>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              8. Legal Aid & Financial Support
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
              AINAA provides access to a Legal Aid Panel and financial support services for eligible members. These services are subject to separate terms and eligibility criteria. AINAA acts as a facilitator and is not a party to any legal proceedings or financial transactions arising from these services. The availability of legal aid and financial support is subject to the Association's resources and may be modified at any time.
            </p>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              9. Limitation of Liability
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
              To the maximum extent permitted by applicable law, AINAA and its officers, directors, employees, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of or inability to use the platform, Trust Score calculations, or services provided by the Association. AINAA's total liability shall not exceed the fees paid by you in the twelve months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              10. Modifications
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
              AINAA reserves the right to modify these Terms of Service at any time. Material changes will be communicated to members via official notices and circulars published on the platform. Continued use of AINAA's services following the publication of modified terms constitutes acceptance of those terms.
            </p>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              11. Governing Law & Dispute Resolution
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
              These Terms shall be governed by and construed in accordance with applicable law. Any dispute arising from or relating to these Terms or the use of AINAA's services shall first be referred to the Association's internal dispute resolution process. If unresolved, the dispute shall be submitted to binding arbitration in accordance with the rules of the relevant arbitration body.
            </p>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              12. Contact
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
              For questions or concerns regarding these Terms of Service, contact us at{' '}
              <a href="mailto:legal@ainaa.co" className="text-[#0F1B2D] font-semibold hover:text-[#C4A35A] transition-colors underline">
                legal@ainaa.co
              </a>
            </p>
          </section>
        </div>
      </motion.div>

      <Footer />
    </div>
  );
};

export default TermsOfService;
